import { Directive, Injector, OnDestroy } from '@angular/core';
import { Coluna, Exercicio, FormatoExportacao, FuncaoService, GlobalService, Login, ProgressoService, Relatorio } from 'eddydata-lib';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Assinaturas } from '../../../assinaturas/assinaturas';
import { AnexoLRFService } from '../../service/anexo-lrf.service';

@Directive()
export class DespesaPessoalLRF implements OnDestroy {
  protected funcaoService: FuncaoService;
  protected globalService: GlobalService;
  private login: Login = new Login();
  protected unsubscribe: Subject<void> = new Subject();
  public valorDespesaPessoal = 0;
  public diferencaDeMeses = 0;
  public porPeriodo = false;

  constructor(
    private anexoServico: AnexoLRFService,
    private executivo: boolean,
    private mes: number,
    private exercicio: Exercicio,
    protected injector: Injector,
    protected progressoService: ProgressoService,
    protected assinaturaControleInterno: boolean,
    protected percentual: boolean,
    private periodoInicial?: Date,
    private periodoFinal?: Date,
  ) {
    this.globalService = new GlobalService();
    this.funcaoService = new FuncaoService();
    this.login = GlobalService.obterSessaoLogin();
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public async obterDespesaPessoal(orgaos: number[]) {
    const result = await this.anexoServico.obterDespesaPessoal(this.mes, this.exercicio.id, orgaos, true, this.percentual).toPromise();
    return this.conteudo(result);
  }


  // tslint:disable: max-line-length
  public montarRelatorio(orgaos: number[], formato: FormatoExportacao) {
    if (this.periodoInicial && this.periodoFinal) {
      this.diferencaDeMeses = this.diferencaMeses(this.periodoInicial, this.periodoFinal);
    } else {
      this.diferencaDeMeses = 12;
    }

    const parametros: {} = {};

    if (this.mes) {
      parametros['mes'] = this.mes;
    }
    if (this.exercicio) {
      parametros['ano'] = this.exercicio.id;
    }
    this.anexoServico.obterDespesaPessoal(this.mes, this.exercicio.id, orgaos, this.executivo, this.percentual)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(async idTransacao => {
        let dados
        this.progressoService.show(idTransacao, async (retorno) => {
          dados = retorno
          parametros['orgaos'] = orgaos.join();
          if (formato === 'pdf') {
            Relatorio.imprimirPersonalizado(
              `DEMONSTRATIVO DE APURAÇÃO DAS DESPESAS COM PESSOAL - ${this.executivo ? 'PODER EXECUTIVO' : 'PODER LEGISLATIVO'}`,
              this.login.usuario.nome, this.login.usuario.sobrenome,
              this.login.orgao.nome, this.login.brasao,
              await this.conteudo(dados),
              'landscape', `DEMONSTRATIVO DE APURAÇÃO DAS DESPESAS COM PESSOAL - ${this.executivo ? 'PODER EXECUTIVO' : 'PODER LEGISLATIVO'}`,
              {
                linhas: {
                  hLineWidth() {
                    return 1;
                  },
                  vLineWidth() {
                    return 1;
                  },
                  hLineColor() {
                    return 'black';
                  },
                  paddingLeft() {
                    return 3;
                  },
                  paddingRight() {
                    return 3;
                  }
                }
              }, false, false, null, 'Referência: ' + this.globalService.obterDataBR().monthNames[this.mes - 1].toLocaleUpperCase() + '/' + this.exercicio.ano);
          } else {
            this.funcaoService.exportar(formato, this.normalizar(dados), `DEMONSTRATIVO DE APURAÇÃO DAS DESPESAS COM PESSOAL - ${this.executivo ? 'PODER EXECUTIVO' : 'PODER LEGISLATIVO'}`, this.colunas());
          }
        })
      });
  }

  private async conteudo(dados: any[]): Promise<{}[]> {
    const totalMeses = this.diferencaDeMeses || 12;

    const registros: any[] = [];

    const linha1: any[] = [
      {
        text: 'DESPESAS COM PESSOAL',
        rowSpan: 2,
        alignment: 'center',
        bold: true
      },
      {
        text: `EVOLUÇÃO DA DESPESA LÍQUIDA NOS ÚLTIMOS ${totalMeses} ${totalMeses === 1 ? 'MÊS' : 'MESES'}`,
        alignment: 'center',
        bold: true,
        fontSize: 6,
        colSpan: totalMeses
      }
    ];

    for (let i = 1; i < totalMeses; i++) linha1.push('');

    linha1.push({
      text: `Total ${totalMeses} ${totalMeses === 1 ? 'mês' : 'meses'}`,
      rowSpan: 2,
      alignment: 'center',
      bold: true,
      fontSize: 6
    });

    registros.push(linha1);

    const linha2: any[] = [{ text: '' }];

   for (let i = totalMeses - 1; i >= 0; i--) {
      linha2.push({
        text: this.retornarNomeMes(-i),
        alignment: 'center',
        bold: true,
        fontSize: 6
      });
    }

    registros.push(linha2);

    const camposSoma = Array.from({ length: totalMeses }, (_, i) => `soma${i}`);

    const grupos = this.funcaoService.agrupar(dados, 'tipo', camposSoma);

    let somaTotal = 0;

    const despesaPercentual: any = { nome: 'TOTAL DESPESA COM PESSOAL' };
    const rcl: any = { nome: 'RECEITA CORRENTE LIQUIDA', somaTotal: 0 };

    for (let i = totalMeses - 1; i >= 0; i--) {
      despesaPercentual[`soma${i}`] = 0;
      rcl[`soma${i}`] = 0;
    }

    for (const grupo of grupos) {

      let somaGrupo = 0;

      if (grupo.grupo !== 2) {

        for (let i = totalMeses - 1; i >= 0; i--) {
          somaGrupo += +grupo.totalizadores[`soma${i}`];
        }

      } else {

        for (let i = totalMeses - 1; i >= 0; i--) {
          rcl[`soma${i}`] = +grupo.totalizadores[`soma${i}`];
          rcl.somaTotal += +grupo.totalizadores[`soma${i}`];
        }

      }

      somaTotal = somaTotal === 0 ? somaGrupo : somaTotal - somaGrupo;

      for (const registro of grupo.registros || []) {

        const linha: any[] = [{
          text: registro.nome,
          bold: grupo.grupo === 2,
          fontSize: 6
        }];

        let totalRegistro = 0;

        for (let i = totalMeses - 1; i >= 0; i--) {
          const valor = +registro[`soma${i}`] || 0;
          totalRegistro += valor;
          if (this.percentual && grupo.grupo !== 2) {
            if (grupo.grupo === 0)
              despesaPercentual[`soma${i}`] += valor;
            else
              despesaPercentual[`soma${i}`] -= valor;
          }

          linha.push({
            text: this.funcaoService.convertToBrNumber(valor),
            alignment: 'right',
            fontSize: 6,
            bold: grupo.grupo === 2
          });
        }

        linha.push({
          text: this.funcaoService.convertToBrNumber(totalRegistro),
          alignment: 'right',
          fontSize: 6,
          bold: grupo.grupo === 2
        });

        registros.push(linha);
      }

      if (grupo.grupo !== 2) {

        const linhaSubtotal: any[] = [{
          text: Number(grupo.grupo) === 0 ? 'SUBTOTAL (I)' : 'SUBTOTAL (II)',
          bold: true
        }];

        for (let i = totalMeses - 1; i >= 0; i--) {
          linhaSubtotal.push({
            text: this.funcaoService.convertToBrNumber(grupo.totalizadores[`soma${i}`]),
            alignment: 'right',
            fontSize: 6,
            bold: true
          });
        }

        linhaSubtotal.push({
          text: this.funcaoService.convertToBrNumber(somaGrupo),
          alignment: 'right',
          fontSize: 6,
          bold: true
        });

        registros.push(linhaSubtotal);

        const linhaVazia = Array(totalMeses + 2).fill('');
        linhaVazia[0] = { text: ' ', colSpan: totalMeses + 2 };
        registros.push(linhaVazia);
      }

      if (Number(grupo.grupo) === 0) {
        const linhaNaoComputadas = Array(totalMeses + 2).fill('');
        linhaNaoComputadas[0] = {
          text: 'DESPESAS NÃO COMPUTADAS',
          bold: true,
          fontSize: 6,
          colSpan: totalMeses + 2
        };
        registros.push(linhaNaoComputadas);
      }
    }

    if (this.percentual) {

      const linhaDespesa: any[] = [{
        text: despesaPercentual.nome,
        bold: true,
        fontSize: 6
      }];

      let totalDespesa = 0;

      for (let i = totalMeses - 1; i >= 0; i--) {
        totalDespesa += despesaPercentual[`soma${i}`];

        linhaDespesa.push({
          text: this.funcaoService.convertToBrNumber(despesaPercentual[`soma${i}`]),
          alignment: 'right',
          fontSize: 6,
          bold: true
        });
      }

      linhaDespesa.push({
        text: this.funcaoService.convertToBrNumber(totalDespesa),
        alignment: 'right',
        fontSize: 6,
        bold: true
      });

      registros.push(linhaDespesa);

      const linhaPercentual: any[] = [{
        text: 'PERCENTUAL LRF',
        bold: true,
        fontSize: 6
      }];

      for (let i = totalMeses - 1; i >= 0; i--) {

        const percentual = rcl[`soma${i}`]
          ? (despesaPercentual[`soma${i}`] / rcl[`soma${i}`]) * 100
          : 0;

        linhaPercentual.push({
          text: this.funcaoService.convertToBrNumber(percentual),
          alignment: 'right',
          fontSize: 6,
          bold: true
        });
      }

      const percentualTotal = rcl.somaTotal
        ? (totalDespesa / rcl.somaTotal) * 100
        : 0;

      linhaPercentual.push({
        text: this.funcaoService.convertToBrNumber(percentualTotal),
        alignment: 'right',
        fontSize: 6,
        bold: true
      });

      registros.push(linhaPercentual);
    }

    const linhaTotal = Array(totalMeses + 2).fill('');

    linhaTotal[0] = {
      text: 'TOTAL LÍQUIDO ( I - II )',
      bold: true,
      fontSize: 6,
      colSpan: totalMeses + 1
    };

    linhaTotal[totalMeses + 1] = {
      text: this.funcaoService.convertToBrNumber(somaTotal),
      alignment: 'right',
      fontSize: 6,
      bold: true
    };

    registros.push(linhaTotal);

    this.valorDespesaPessoal = somaTotal;

    const ass = new Assinaturas(this.login.orgao, this.injector);

    const assinaturas = await ass.dadosAssinatura(
      50,
      false,
      null,
      this.assinaturaControleInterno,
      false,
      false,
      `${this.funcaoService.ultimoDiaMes(this.mes)}/${this.funcaoService.strZero(this.mes, 2)}/${this.exercicio.ano}`
    );

    const widths = ['*'];
    for (let i = totalMeses - 1; i >= 0; i--) widths.push('auto');
    widths.push('auto');

    return [
      {
        layout: 'linhas',
        table: {
          dontBreakRows: true,
          headerRows: 2,
          widths,
          body: registros
        }
      },
      {
        layout: 'linhas',
        table: {
          dontBreakRows: true,
          headerRows: 0,
          widths: ['*', '*', '*'],
          body: assinaturas
        }
      }
    ];
  }

  private retornarNomeMes(diminuir: number) {
    const fator: number = +this.mes + +diminuir;
    if (fator > 0) {
      return ('00' + fator).slice(-2)
        + `/ ${this.exercicio.ano}`;
    } else {
      const meses: number = +12 + +fator;
      return ('00' + meses).slice(-2) + `/ ${+ this.exercicio.ano - 1}`;
    }
  }

  public normalizar(lista: any[]) {
    const listaExportar = []

    let somaTotal = 0.0;

    let soma11 = 0;
    let soma10 = 0;
    let soma9 = 0;
    let soma8 = 0;
    let soma7 = 0;
    let soma6 = 0;
    let soma5 = 0;
    let soma4 = 0;
    let soma3 = 0;
    let soma2 = 0;
    let soma1 = 0;
    let soma0 = 0;

    const grupos = this.funcaoService.agrupar(lista, 'tipo',
      ['soma0', 'soma1', 'soma2', 'soma3', 'soma4', 'soma5', 'soma6', 'soma7', 'soma8', 'soma9', 'soma10', 'soma11']);
    for (const grupo of grupos) {
      let somaGrupo = 0.0;
      for (let idx = 11; idx >= 0; idx--) {
        somaGrupo += +grupo.totalizadores[`soma${idx}`];
      }
      if (somaTotal === 0.0) {
        somaTotal += somaGrupo;
      } else {
        somaTotal -= somaGrupo;
      }

      for (const item of grupo.registros) {
        const items = {
          nome_despesa: item.nome,
          mes11: this.funcaoService.convertToBrNumber(item.soma11),
          mes10: this.funcaoService.convertToBrNumber(item.soma10),
          mes9: this.funcaoService.convertToBrNumber(item.soma9),
          mes8: this.funcaoService.convertToBrNumber(item.soma8),
          mes7: this.funcaoService.convertToBrNumber(item.soma7),
          mes6: this.funcaoService.convertToBrNumber(item.soma6),
          mes5: this.funcaoService.convertToBrNumber(item.soma5),
          mes4: this.funcaoService.convertToBrNumber(item.soma4),
          mes3: this.funcaoService.convertToBrNumber(item.soma3),
          mes2: this.funcaoService.convertToBrNumber(item.soma2),
          mes1: this.funcaoService.convertToBrNumber(item.soma1),
          mes0: this.funcaoService.convertToBrNumber(item.soma0),
          total_12_meses: this.funcaoService.convertToBrNumber((+item.soma11 + +item.soma10 + +item.soma9 + +item.soma8
            + +item.soma7 + +item.soma6 + +item.soma5 + +item.soma4
            + +item.soma3 + +item.soma2 + +item.soma1 + +item.soma0))
        }
        listaExportar.push(items)
      }

      soma11 = grupo.totalizadores['soma11'];
      soma10 = grupo.totalizadores['soma10'];
      soma9 = grupo.totalizadores['soma9'];
      soma8 = grupo.totalizadores['soma8'];
      soma7 = grupo.totalizadores['soma7'];
      soma6 = grupo.totalizadores['soma6'];
      soma5 = grupo.totalizadores['soma5'];
      soma4 = grupo.totalizadores['soma4'];
      soma3 = grupo.totalizadores['soma3'];
      soma2 = grupo.totalizadores['soma2'];
      soma1 = grupo.totalizadores['soma1'];
      soma0 = grupo.totalizadores['soma0'];

      const subtotal1 = {
        nome_despesa: Number(grupo.grupo) === 0 ? 'SUBTOTAL (I)' : 'SUBTOTAL (II)',
        mes11: this.funcaoService.convertToBrNumber(soma11),
        mes10: this.funcaoService.convertToBrNumber(soma10),
        mes9: this.funcaoService.convertToBrNumber(soma9),
        mes8: this.funcaoService.convertToBrNumber(soma8),
        mes7: this.funcaoService.convertToBrNumber(soma7),
        mes6: this.funcaoService.convertToBrNumber(soma6),
        mes5: this.funcaoService.convertToBrNumber(soma5),
        mes4: this.funcaoService.convertToBrNumber(soma4),
        mes3: this.funcaoService.convertToBrNumber(soma3),
        mes2: this.funcaoService.convertToBrNumber(soma2),
        mes1: this.funcaoService.convertToBrNumber(soma1),
        mes0: this.funcaoService.convertToBrNumber(soma0),
        total_12_meses: this.funcaoService.convertToBrNumber(somaGrupo)
      }
      listaExportar.push(subtotal1)

      if (Number(grupo.grupo) === 0) {
        const despesas_nao_computadas = {
          nome_despesa: 'DESPESAS NÃO COMPUTADAS',
          mes11: '',
          mes10: '',
          mes9: '',
          mes8: '',
          mes7: '',
          mes6: '',
          mes5: '',
          mes4: '',
          mes3: '',
          mes2: '',
          mes1: '',
          mes0: '',
          total_12_meses: ''
        }
        listaExportar.push(despesas_nao_computadas)
      }
    }

    const total = {
      nome_despesa: 'TOTAL LÍQUIDO ( I - II )',
      mes11: '',
      mes10: '',
      mes9: '',
      mes8: '',
      mes7: '',
      mes6: '',
      mes5: '',
      mes4: '',
      mes3: '',
      mes2: '',
      mes1: '',
      mes0: '',
      total_12_meses: this.funcaoService.convertToBrNumber(somaTotal)
    }
    listaExportar.push(total)

    return listaExportar
  }

  colunas(): Coluna[] {
    const colunasDefault: Coluna[] = [
      { titulo: 'DESPESAS COM PESSOAL', coluna: 'nome_despesa', bold: true },
      { titulo: this.retornarNomeMes(-11), coluna: 'mes11' },
      { titulo: this.retornarNomeMes(-10), coluna: 'mes10' },
      { titulo: this.retornarNomeMes(-9), coluna: 'mes9' },
      { titulo: this.retornarNomeMes(-8), coluna: 'mes8' },
      { titulo: this.retornarNomeMes(-7), coluna: 'mes7' },
      { titulo: this.retornarNomeMes(-6), coluna: 'mes6' },
      { titulo: this.retornarNomeMes(-5), coluna: 'mes5' },
      { titulo: this.retornarNomeMes(-4), coluna: 'mes4' },
      { titulo: this.retornarNomeMes(-3), coluna: 'mes3' },
      { titulo: this.retornarNomeMes(-2), coluna: 'mes2' },
      { titulo: this.retornarNomeMes(-1), coluna: 'mes1' },
      { titulo: this.retornarNomeMes(0), coluna: 'mes0' },
      { titulo: 'Total 12 meses', coluna: 'total_12_meses' },
    ];
    return colunasDefault;
  }

  private diferencaMeses(dataInicial, dataFinal) {
    const anoInicial = dataInicial.getFullYear();
    const mesInicial = dataInicial.getMonth();

    const anoFinal = dataFinal.getFullYear();
    const mesFinal = dataFinal.getMonth();

    return ((anoFinal - anoInicial) * 12 + (mesFinal - mesInicial)) + 1;
  }

}
